/**
 * Auth Store — single source of truth for authentication state.
 *
 * Uses Zustand with an explicit state-machine model.
 * Every transition is a named action that produces a well-typed next state.
 * No magic booleans — the discriminated union on `status` drives all UI.
 *
 * Replaces:
 *  - useInitializationStatus  (isInitialized / isLoggedIn booleans)
 *  - useAuthState             (mixed concerns hook)
 */

import { create } from "zustand"

import { AUTH_STATUS } from "./auth-types"
import type { AuthError, AuthSession, AuthState } from "./auth-types"

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

interface AuthStoreActions {
  /** Transition → RESTORING_SESSION */
  startSessionRestore: () => void
  /** Transition → AUTHENTICATING */
  startAuthentication: () => void
  /** Transition → AUTHENTICATED with a valid session */
  setAuthenticated: (session: AuthSession) => void
  /** Transition → UNAUTHENTICATED (no error) */
  setUnauthenticated: () => void
  /** Transition → ERROR with structured error info */
  setError: (error: AuthError) => void
  /** Full reset back to UNINITIALIZED (used by logout) */
  reset: () => void
}

type AuthStore = AuthState & AuthStoreActions

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL_STATE: AuthState = {
  status: AUTH_STATUS.UNINITIALIZED,
  session: null,
  error: null,
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const useAuthStore = create<AuthStore>()((set) => ({
  ...INITIAL_STATE,

  startSessionRestore: () =>
    set({
      status: AUTH_STATUS.RESTORING_SESSION,
      session: null,
      error: null,
    }),

  startAuthentication: () =>
    set({
      status: AUTH_STATUS.AUTHENTICATING,
      session: null,
      error: null,
    }),

  setAuthenticated: (session: AuthSession) =>
    set({
      status: AUTH_STATUS.AUTHENTICATED,
      session,
      error: null,
    }),

  setUnauthenticated: () =>
    set({
      status: AUTH_STATUS.UNAUTHENTICATED,
      session: null,
      error: null,
    }),

  setError: (error: AuthError) =>
    set({
      status: AUTH_STATUS.ERROR,
      session: null,
      error,
    }),

  reset: () => set(INITIAL_STATE),
}))

export { useAuthStore }
